import { useState } from "react";
import { Lightbulb } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { ReportShell, Stat, Table, money } from "./ReportShell";

const STATUS_TONE = {
  "In stock": "text-emerald-400",
  "In Stock": "text-emerald-400",
  "Available": "text-emerald-400",
  "Reserved": "text-blue-400",
  "Low stock": "text-amber-400",
  "Back order": "text-red-400",
  "Special order": "text-purple-300",
};

const Pill = ({ value }) => (
  <span className={`font-semibold ${STATUS_TONE[value] || "text-zinc-300"}`}>{value}</span>
);

const Rec = ({ value }) =>
  value ? (
    <span className="text-[11px] font-semibold text-amber-300">{value}</span>
  ) : (
    <span className="text-zinc-600">—</span>
  );

const Section = ({ title, count, children }) => (
  <div className="mt-8 first:mt-6">
    <h2 className="mb-2 text-sm font-bold uppercase tracking-widest text-zinc-300">
      {title} <span className="text-zinc-600">({count})</span>
    </h2>
    {children}
  </div>
);

export const StockDetailReport = () => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/reports/stock-detail");
      setData(data);
    } finally {
      setBusy(false);
    }
  };

  const t = data?.totals;
  const slabs = data?.slabs || [];
  const remnants = data?.remnants || [];
  const sinks = data?.sinks || [];
  const recs = data?.recommendations || [];

  const csvRows = [
    ...slabs.map((r) => ({
      type: "Slab", code: r.code, item: r.color, detail: `${r.material} ${r.thickness} ${r.size}`,
      status: r.status, stock: r.qty, cost: r.cost_per_sqft ?? "", sell: r.sell_per_sqft ?? "",
      margin_pct: r.margin_pct ?? "", value_cost: r.value_cost, value_sell: r.value_sell,
      recommendation: r.recommendation,
    })),
    ...remnants.map((r) => ({
      type: "Remnant", code: r.code, item: r.color, detail: `${r.material} ${r.thickness} ${r.size}`,
      status: r.status, stock: r.usable_sqft, cost: "", sell: r.sell_per_sqft ?? "",
      margin_pct: "", value_cost: "", value_sell: r.value_sell, recommendation: r.recommendation,
    })),
    ...sinks.map((r) => ({
      type: "Sink", code: r.code, item: r.label, detail: r.sink_type, status: r.status,
      stock: r.qty, cost: r.cost ?? "", sell: r.sell ?? "", margin_pct: r.margin_pct ?? "",
      value_cost: r.value_cost, value_sell: r.value_sell, recommendation: r.recommendation,
    })),
  ];

  return (
    <ReportShell
      id="stock-detail"
      title="Stock Detail"
      blurb="Every slab, remnant and sink — current stock, your cost, sell price, back/special order and what to do next."
      onGenerate={generate}
      busy={busy}
      generated={!!data}
      csvRows={csvRows}
      csvName="stock-detail.csv"
    >
      {t && (
        <>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
            <Stat label="Stock at Cost" value={money(t.stock_cost_value)} tone="text-zinc-200" testId="report-stat-cost-value" />
            <Stat label="Stock at Sell" value={money(t.stock_sell_value)} tone="text-yellow-400" testId="report-stat-sell-value" />
            <Stat label="Potential Profit" value={money(t.potential_profit)} tone="text-emerald-400" testId="report-stat-profit" />
            <Stat label="Back Order" value={t.back_order} tone={t.back_order ? "text-red-400" : "text-zinc-200"} testId="report-stat-backorder" />
            <Stat label="Low Stock" value={t.low_stock} tone={t.low_stock ? "text-amber-400" : "text-zinc-200"} testId="report-stat-lowstock" />
            <Stat label="Special Order" value={t.special_order} tone="text-purple-300" testId="report-stat-special" />
          </div>

          <div
            data-testid="report-recommendations"
            className="mt-4 rounded-lg border border-amber-500/25 bg-amber-500/5 p-4"
          >
            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-amber-300">
              <Lightbulb size={15} weight="bold" /> Recommendations
            </div>
            <ul className="mt-2 space-y-1">
              {recs.map((r, i) => (
                <li key={i} data-testid={`report-rec-${i}`} className="text-sm text-zinc-200">
                  • {r}
                </li>
              ))}
            </ul>
          </div>

          <Section title="Slabs" count={slabs.length}>
            <Table
              testId="report-slabs"
              rowKey={(r) => r.code || `${r.color}-${Math.random()}`}
              rows={slabs}
              empty="No slabs in stock."
              columns={[
                { key: "color", label: "Color", render: (r) => (
                  <div className="min-w-[150px]">
                    <div className="truncate font-semibold">{r.color}</div>
                    <div className="text-[11px] text-zinc-500">{r.material} · {r.thickness} · {r.size}</div>
                  </div>
                ) },
                { key: "status", label: "Status", render: (r) => <Pill value={r.status} /> },
                { key: "qty", label: "Qty", right: true },
                { key: "open_quotes", label: "Quotes", right: true, render: (r) => r.open_quotes ? <span className="text-indigo-300">{r.open_quotes}</span> : <span className="text-zinc-600">0</span> },
                { key: "sqft", label: "ft²", right: true },
                { key: "cost_per_sqft", label: "Cost/ft²", right: true, render: (r) => r.cost_per_sqft != null ? money(r.cost_per_sqft) : <span className="text-red-400">—</span> },
                { key: "sell_per_sqft", label: "Sell/ft²", right: true, render: (r) => r.sell_per_sqft != null ? money(r.sell_per_sqft) : <span className="text-red-400">—</span> },
                { key: "margin_pct", label: "Margin", right: true, render: (r) => r.margin_pct != null ? `${r.margin_pct}%` : "—" },
                { key: "value_cost", label: "Value @cost", right: true, render: (r) => money(r.value_cost) },
                { key: "recommendation", label: "Recommendation", render: (r) => <Rec value={r.recommendation} /> },
              ]}
            />
          </Section>

          <Section title="Remnants" count={remnants.length}>
            <Table
              testId="report-remnants"
              rowKey={(r) => r.code || `${r.color}-${Math.random()}`}
              rows={remnants}
              empty="No remnants in stock."
              columns={[
                { key: "color", label: "Color", render: (r) => (
                  <div className="min-w-[150px]">
                    <div className="truncate font-semibold">{r.color}</div>
                    <div className="text-[11px] text-zinc-500">{r.material} · {r.thickness} · {r.size}</div>
                  </div>
                ) },
                { key: "status", label: "Status", render: (r) => <Pill value={r.status} /> },
                { key: "usable_sqft", label: "Usable ft²", right: true, render: (r) => <span className="text-blue-400">{r.usable_sqft}</span> },
                { key: "sell_per_sqft", label: "Sell/ft²", right: true, render: (r) => r.sell_per_sqft != null ? money(r.sell_per_sqft) : <span className="text-red-400">—</span> },
                { key: "value_sell", label: "Value @sell", right: true, render: (r) => money(r.value_sell) },
                { key: "location", label: "Location" },
                { key: "recommendation", label: "Recommendation", render: (r) => <Rec value={r.recommendation} /> },
              ]}
            />
          </Section>

          <Section title="Sinks" count={sinks.length}>
            <Table
              testId="report-sinks"
              rowKey={(r) => r.code || r.label}
              rows={sinks}
              empty="No sinks in stock."
              columns={[
                { key: "label", label: "Sink", render: (r) => (
                  <div className="min-w-[150px]">
                    <div className="truncate font-semibold">{r.label}</div>
                    <div className="text-[11px] text-zinc-500">{r.sink_type}{r.supplier ? ` · ${r.supplier}` : ""}</div>
                  </div>
                ) },
                { key: "status", label: "Status", render: (r) => <Pill value={r.status} /> },
                { key: "qty", label: "On hand", right: true },
                { key: "reserved", label: "Reserved", right: true, render: (r) => r.reserved ? <span className="text-indigo-300">{r.reserved}</span> : "0" },
                { key: "available", label: "Free", right: true },
                { key: "min_level", label: "Min", right: true },
                { key: "cost", label: "Cost", right: true, render: (r) => r.cost != null ? money(r.cost) : <span className="text-red-400">—</span> },
                { key: "sell", label: "Sell", right: true, render: (r) => r.sell != null ? money(r.sell) : <span className="text-red-400">—</span> },
                { key: "margin_pct", label: "Margin", right: true, render: (r) => r.margin_pct != null ? `${r.margin_pct}%` : "—" },
                { key: "recommendation", label: "Recommendation", render: (r) => <Rec value={r.recommendation} /> },
              ]}
            />
          </Section>
        </>
      )}
    </ReportShell>
  );
};
